/**
 * CustomerMergeDialog - Dialog to merge two customer accounts
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ArrowRight, Loader2, CheckCircle, Users, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Suchbare Kunden-Combobox.
 * Sucht über Name, E-Mail, Telefon, Kundennummer (case-insensitive).
 * Zeigt maximal 50 Treffer — bei mehr fordert sie zur Verfeinerung auf.
 */
function CustomerCombobox({ value, onChange, customers, excludeId, placeholder, testIdPrefix }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selected = useMemo(
    () => customers.find((c) => c.id === value),
    [customers, value]
  );

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const candidates = customers.filter((c) => c.id !== excludeId);
    if (!q) return candidates.slice(0, 50);
    return candidates
      .filter((c) => {
        const haystack = [
          c.first_name,
          c.last_name,
          `${c.first_name || ''} ${c.last_name || ''}`,
          c.email,
          c.phone,
          c.mobile,
          c.customer_number,
          c.id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 50);
  }, [customers, query, excludeId]);

  const displayLabel = (c) =>
    `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || '–';

  return (
    <div ref={containerRef} className="relative">
      {selected ? (
        <div
          className="w-full mt-1 p-2 border rounded-md flex items-center justify-between gap-2 bg-white text-sm"
          data-testid={`${testIdPrefix}-selected`}
        >
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {displayLabel(selected)}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {[selected.email, selected.phone || selected.mobile, selected.customer_number]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onChange('');
              setQuery('');
              setOpen(true);
            }}
            className="text-gray-400 hover:text-gray-600 p-1"
            data-testid={`${testIdPrefix}-clear`}
            aria-label="Auswahl zurücksetzen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="relative mt-1">
          <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder={placeholder || 'Name, E-Mail, Telefon, Kundennummer …'}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className="pl-8"
            data-testid={`${testIdPrefix}-search`}
          />
        </div>
      )}

      {open && !selected && (
        <div
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white border rounded-md shadow-lg"
          data-testid={`${testIdPrefix}-results`}
        >
          {filtered.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">
              Keine Kunden gefunden.
            </div>
          ) : (
            <>
              {filtered.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => {
                    onChange(c.id);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="w-full text-left p-2 hover:bg-blue-50 border-b last:border-b-0 text-sm"
                  data-testid={`${testIdPrefix}-option-${c.id}`}
                >
                  <div className="font-medium text-gray-900 truncate">
                    {displayLabel(c)}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {[c.email, c.phone || c.mobile, c.customer_number]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </button>
              ))}
              {filtered.length === 50 && (
                <div className="p-2 text-xs text-amber-700 bg-amber-50">
                  Mehr als 50 Treffer — bitte Suche verfeinern.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const COLLECTION_LABELS = {
  appointments: 'Termine',
  invoices: 'Rechnungen',
  payments: 'Zahlungen',
  subscriptions: 'Abonnements',
  training_plans: 'Trainingspläne',
  training_entries: 'Trainingseinträge',
  nutrition_entries: 'Ernährungseinträge',
  nutrition_goals: 'Ernährungsziele',
  nutrition_recipes: 'Rezepte',
  body_tracking: 'Körper-Tracking',
  inbody_measurements: 'InBody Messungen',
  anamnese_records: 'Anamnese',
  message_logs: 'Nachrichten',
  chatbot_conversations: 'Chat-Verläufe',
  fitness_workouts: 'Workouts',
  manual_training_entries: 'Manuelle Trainings',
  recurring_subscriptions: 'Serien-Buchungen',
  payrexx_transactions: 'Zahlungstransaktionen',
};

const CustomerMergeDialog = ({ open, onOpenChange, customers, onMergeComplete }) => {
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [step, setStep] = useState('select'); // select, preview, done

  const token = localStorage.getItem('token');

  // Kundenliste liefert als `id` die User-ID — der Merge-Endpoint erwartet
  // die echte Customer-ID (customer_id). Fallback: id (Backend löst beides auf).
  const resolveCustomerId = useCallback((id) => {
    const c = customers.find((x) => x.id === id);
    return c?.customer_id || id;
  }, [customers]);

  const loadPreview = useCallback(async () => {
    if (!sourceId || !targetId) {
      toast.error('Bitte wählen Sie beide Kunden aus');
      return;
    }
    if (sourceId === targetId) {
      toast.error('Quell- und Ziel-Kunde dürfen nicht identisch sein');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/customers/merge/preview`, {
        source_customer_id: resolveCustomerId(sourceId),
        target_customer_id: resolveCustomerId(targetId),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPreview(res.data);
      setStep('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Laden der Vorschau');
    } finally {
      setLoading(false);
    }
  }, [sourceId, targetId, token, resolveCustomerId]);

  const executeMerge = useCallback(async () => {
    setMerging(true);
    try {
      const res = await axios.post(`${API}/admin/customers/merge`, {
        source_customer_id: resolveCustomerId(sourceId),
        target_customer_id: resolveCustomerId(targetId),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setStep('done');
      if (onMergeComplete) onMergeComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zusammenführen');
    } finally {
      setMerging(false);
    }
  }, [sourceId, targetId, token, onMergeComplete, resolveCustomerId]);

  const handleClose = () => {
    setStep('select');
    setPreview(null);
    setSourceId('');
    setTargetId('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" aria-describedby="merge-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Kunden zusammenführen
          </DialogTitle>
          <DialogDescription id="merge-description">
            Übertragen Sie alle Daten von einem Kunden auf einen anderen.
          </DialogDescription>
        </DialogHeader>

        {step === 'select' && (
          <div className="space-y-4" data-testid="merge-select-step">
            <div>
              <Label className="text-sm font-medium">Quell-Kunde (wird gelöscht)</Label>
              <CustomerCombobox
                value={sourceId}
                onChange={setSourceId}
                customers={customers}
                excludeId={targetId}
                testIdPrefix="merge-source"
              />
            </div>

            <div className="flex justify-center">
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </div>

            <div>
              <Label className="text-sm font-medium">Ziel-Kunde (behält Konto)</Label>
              <CustomerCombobox
                value={targetId}
                onChange={setTargetId}
                customers={customers}
                excludeId={sourceId}
                testIdPrefix="merge-target"
              />
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <p className="font-medium">Achtung:</p>
                  <p>Der Quell-Kunde wird nach der Zusammenführung <strong>komplett gelöscht</strong>. Punkte und Guthaben werden NICHT übertragen.</p>
                </div>
              </div>
            </div>

            <Button
              onClick={loadPreview}
              disabled={!sourceId || !targetId || loading}
              className="w-full"
              data-testid="merge-preview-btn"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Lade Vorschau...</> : 'Vorschau anzeigen'}
            </Button>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4" data-testid="merge-preview-step">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-600 font-medium">Quell-Kunde (wird gelöscht)</p>
                <p className="font-semibold text-sm mt-1">{preview.source.name}</p>
                <p className="text-xs text-gray-500">{preview.source.email}</p>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-600 font-medium">Ziel-Kunde (behält Konto)</p>
                <p className="font-semibold text-sm mt-1">{preview.target.name}</p>
                <p className="text-xs text-gray-500">{preview.target.email}</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">
                Wird übertragen: <Badge variant="secondary">{preview.total_records} Datensätze</Badge>
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {Object.entries(preview.transfer_summary).map(([key, count]) => (
                  <div key={key} className="flex justify-between text-sm py-1 px-2 bg-gray-50 rounded">
                    <span>{COLLECTION_LABELS[key] || key}</span>
                    <Badge variant="outline" className="text-xs">{count}</Badge>
                  </div>
                ))}
                {Object.keys(preview.transfer_summary).length === 0 && (
                  <p className="text-sm text-gray-500 italic">Keine Datensätze zum Übertragen</p>
                )}
              </div>
            </div>

            {Object.keys(preview.skip_summary).length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 font-medium mb-1">Wird NICHT übertragen:</p>
                {Object.entries(preview.skip_summary).map(([key, count]) => (
                  <span key={key} className="text-xs text-gray-500">{COLLECTION_LABELS[key] || key} ({count}), </span>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('select')} className="flex-1">
                Zurück
              </Button>
              <Button
                onClick={executeMerge}
                disabled={merging}
                variant="destructive"
                className="flex-1"
                data-testid="merge-confirm-btn"
              >
                {merging ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Führe zusammen...</> : 'Zusammenführen'}
              </Button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-6 space-y-3" data-testid="merge-done-step">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <p className="font-semibold">Kunden erfolgreich zusammengeführt!</p>
            <p className="text-sm text-gray-500">Der Quell-Kunde wurde gelöscht und alle Daten übertragen.</p>
            <Button onClick={handleClose} className="mt-4">Schliessen</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerMergeDialog;
